import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useActiveTenant } from "../tenant/TenantContext";
import { useTenantMutation, useTenantQuery } from "../hooks/useTenantData";
import { can } from "../tenant/permissions";
import {
  INVITATION_STATUS_LABELS,
  occupancyValidity,
  type Invitation,
  type InvitationStatus,
} from "../api/types";
import {
  Badge,
  ConfirmDialog,
  InlineError,
  PageHeader,
  Panel,
  QueryState,
} from "../components/ui";

const STATUS_TONES: Record<InvitationStatus, "warn" | "ok" | "muted" | "danger"> = {
  pending: "warn",
  used: "ok",
  expired: "muted",
  revoked: "danger",
};

export function InvitationsPage() {
  const membership = useActiveTenant();
  const canManage = can(membership.role, "invitations.manage");
  const invitations = useTenantQuery(["invitations"], (api, t) => api.listInvitations(t));
  const occupancies = useTenantQuery(["occupancies"], (api, t) => api.listOccupancies(t));

  const [selectedOccupancy, setSelectedOccupancy] = useState("");
  const [showing, setShowing] = useState<Invitation | null>(null);
  const [revoking, setRevoking] = useState<Invitation | null>(null);
  const [copied, setCopied] = useState(false);

  const generate = useTenantMutation<string, Invitation>(
    (api, t, occupancyId) => api.createInvitation(t, occupancyId),
    [["invitations"], ["audit-events"]],
  );
  const resend = useTenantMutation<string, Invitation>(
    (api, t, id) => api.resendInvitation(t, id),
    [["invitations"], ["audit-events"]],
  );
  const revoke = useTenantMutation<string, Invitation>(
    (api, t, id) => api.revokeInvitation(t, id),
    [["invitations"], ["audit-events"]],
  );

  // O link de ativação nunca é registrado em logs; a cópia usa apenas a área
  // de transferência.
  async function copyLink(invitation: Invitation) {
    if (!invitation.activation_url) return;
    await navigator.clipboard.writeText(invitation.activation_url);
    setCopied(true);
  }

  const eligibleOccupancies =
    occupancies.data?.filter(
      (o) => o.can_activate_app && occupancyValidity(o) !== "expired" && occupancyValidity(o) !== "inactive",
    ) ?? [];

  return (
    <>
      <PageHeader
        title="Convites do App AVA"
        subtitle="Cada morador ativa o aplicativo com a própria conta. Uma unidade pode ter vários moradores e aparelhos."
      />
      {canManage && (
        <Panel title="Gerar convite individual">
          <div className="form-grid">
            <label>
              Vínculo (pessoa · unidade)
              <select value={selectedOccupancy} onChange={(e) => setSelectedOccupancy(e.target.value)}>
                <option value="">Selecione</option>
                {eligibleOccupancies.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.person_name} · {o.block_name} {o.unit_label}
                  </option>
                ))}
              </select>
            </label>
            <div className="form-actions">
              <button
                type="button"
                disabled={!selectedOccupancy || generate.isPending}
                onClick={() =>
                  generate.mutate(selectedOccupancy, {
                    onSuccess: (invitation) => {
                      setCopied(false);
                      setShowing(invitation);
                      setSelectedOccupancy("");
                    },
                  })
                }
              >
                {generate.isPending ? "Gerando…" : "Gerar convite"}
              </button>
            </div>
          </div>
          <p>
            O convite é individual, de uso único e com validade curta. Somente vínculos com permissão de
            ativar o aplicativo aparecem na lista.
          </p>
          <InlineError error={generate.error} />
        </Panel>
      )}
      <Panel title="Convites emitidos">
        <QueryState query={invitations} empty="Nenhum convite emitido ainda.">
          {(items) => (
            <div className="table">
              {items.map((invitation) => (
                <div className="table-row" key={invitation.id}>
                  <span>
                    <strong>{invitation.person_name}</strong>
                    <small>
                      Unidade {invitation.unit_label} · expira em {formatDateTime(invitation.expires_at)}
                    </small>
                  </span>
                  <Badge tone={STATUS_TONES[invitation.status]}>
                    {INVITATION_STATUS_LABELS[invitation.status]}
                  </Badge>
                  <span className="row-actions">
                    {/* O link/QR dá acesso à ativação: visível só para quem gerencia convites. */}
                    {canManage && invitation.status === "pending" && invitation.activation_url && (
                      <button
                        type="button"
                        className="secondary"
                        onClick={() => {
                          setCopied(false);
                          setShowing(invitation);
                        }}
                      >
                        Ver QR e link
                      </button>
                    )}
                    {canManage && (invitation.status === "pending" || invitation.status === "expired") && (
                      <button
                        type="button"
                        className="secondary"
                        disabled={resend.isPending}
                        onClick={() =>
                          resend.mutate(invitation.id, {
                            onSuccess: (fresh) => {
                              setCopied(false);
                              setShowing(fresh);
                            },
                          })
                        }
                      >
                        Reenviar
                      </button>
                    )}
                    {canManage && invitation.status === "pending" && (
                      <button type="button" className="danger" onClick={() => setRevoking(invitation)}>
                        Revogar
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </QueryState>
        <p>
          Reenviar gera um convite novo e invalida o anterior. Convites expirados ou revogados não ativam o
          aplicativo.
        </p>
        <InlineError error={resend.error} />
        <InlineError error={revoke.error} />
      </Panel>

      {showing?.activation_url && (
        <div className="modal" role="presentation" onClick={() => setShowing(null)}>
          <div
            className="modal-card invite-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Convite de ativação para ${showing.person_name}`}
            onClick={(event) => event.stopPropagation()}
          >
            <span className="eyebrow">CONVITE DE ATIVAÇÃO</span>
            <h2>{showing.person_name} · Unidade {showing.unit_label}</h2>
            <QRCodeSVG value={showing.activation_url} size={220} level="M" />
            <p className="invite-link">{showing.activation_url}</p>
            {showing.pin && (
              <p>
                PIN: <strong>{showing.pin}</strong>
              </p>
            )}
            <p>Válido até {formatDateTime(showing.expires_at)}. Uso único e pessoal.</p>
            <div className="modal-actions">
              <button type="button" className="secondary" onClick={() => void copyLink(showing)}>
                {copied ? "Link copiado" : "Copiar link"}
              </button>
              <button type="button" onClick={() => setShowing(null)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={revoking !== null}
        title="Revogar convite"
        description={
          revoking
            ? `Revogar o convite de ${revoking.person_name} (unidade ${revoking.unit_label})? O link e o QR Code deixam de funcionar imediatamente.`
            : ""
        }
        confirmLabel="Revogar convite"
        busy={revoke.isPending}
        onCancel={() => setRevoking(null)}
        onConfirm={() => {
          if (revoking) revoke.mutate(revoking.id, { onSettled: () => setRevoking(null) });
        }}
      />
    </>
  );
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR");
}
